import { parse } from 'mathjs';

import { TestUtil } from './TestUtil';

export function Calculator() {}

Calculator.VERBOSE = false;

Calculator.ADD = 1;
Calculator.SUB = 2;
Calculator.MUL = 3;
Calculator.DIV = 4;
Calculator.SQUR = 5;
Calculator.SQRT = 6;
Calculator.CUBE = 7;
Calculator.CBRT = 8;

Calculator.isDefined = function (value) {
  return typeof value != 'undefined';
};
Calculator.getInt = function (value, def = 0) {
  if (def == null) def = 0;

  let val = parseInt(value);
  if ('' + val == 'NaN') val = def;
  return val;
};
Calculator.getBool = function (value) {
  let val = false;

  if (typeof value === 'string') {
    val = value.toLowerCase() == 'true' || Number(value) == 1 ? true : false;
  }

  if (typeof value === 'number') {
    val = value == 1 ? true : false;
  }

  return val;
};
Calculator.isNumber = function (value) {
  if (Calculator.VERBOSE) TestUtil.log('Calculator.isNumber::', value);
  if (Calculator.isFraction(value)) {
    return Calculator.isNumber(value[0]) && Calculator.isNumber(value[1]);
  } else {
    return isFinite(value); //!isNaN(value);
  }
};
Calculator.isInt = function (value) {
  return value % 1 === 0;
};
Calculator.isFraction = function (f) {
  if (f == null) return false;
  else return typeof f != 'string' && f.length == 2;
};

/* Math */
Calculator.gcd = function (a, b) {
  if (b == 0) return a;
  else return Calculator.gcd(b, a % b);
};
Calculator.getScale = function (number) {
  let exp = Math.floor(Math.log(Math.abs(number)) / Math.LN10);
  if (Math.pow(10, exp + 1) == Math.abs(number)) exp++; //Needed to fix rounding errors
  return exp;
};
Calculator.getPrecision = function (number) {
  const n = Math.abs(number);
  //BAD BUG: n = 0 caused infinite loop.
  if (n == 0) {
    return 0;
  }
  let precision = -Calculator.getScale(n);
  while (Calculator.round(n, precision) != n) {
    precision++;
  }
  return precision;
};
Calculator.getDirection = function (number) {
  if (number > 0) return 1;
  else if (number < 0) return -1;
  else return 0;
};
Calculator.getFactors = function (number, inclusive = true) {
  inclusive = inclusive != null ? inclusive : true;

  if (number < 1) return [];
  else if (number == 1) return [1];
  else {
    const factors: number[] = [];
    if (inclusive) factors.push(1);
    for (let i = 2; i < number; i++) {
      if (number % i == 0) factors.push(i);
    }
    if (inclusive) factors.push(number);
    return factors;
  }
};
Calculator.isPrime = function (number) {
  return Calculator.getFactors(number).length == 2;
};
Calculator.round = function (number, precision = 0) {
  if (precision == null) precision = 0;
  const factor = Math.pow(10, precision);
  number = Math.round(number * factor) / factor;
  return number;
};
Calculator.ceil = function (number, precision = 0) {
  if (precision == null) precision = 0;
  const factor = Math.pow(10, precision);
  number = Math.ceil(number * factor) / factor;
  return number;
};
Calculator.floor = function (number, precision = 0) {
  if (precision == null) precision = 0;
  const factor = Math.pow(10, precision);
  number = Math.floor(number * factor) / factor;
  return number;
};
Calculator.bound = function (number, min, max) {
  return Math.max(Math.min(number, max), min);
};

/* Bitstrings */

Calculator.setBit = function (i, bit, value?) {
  if (!Calculator.isDefined(value)) value = true;
  if (value) return i | (1 << bit);
  else return i & ~(1 << bit);
};
Calculator.bitIsSet = function (i, bit?) {
  if (!Calculator.isDefined(bit)) bit = 0;
  return ((i >> bit) & 1) > 0;
};

/* Fractions */
Calculator.fractionAdd = function (f1, f2, simplify = true) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  const d = f1[1] * f2[1];
  let answer = [f2[1] * f1[0] + f1[1] * f2[0], d];
  answer = Calculator.fractionSimplify(answer);
  return answer;
};
Calculator.fractionSubtract = function (f1, f2, simplify = true) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  const f3 = [-1 * f2[0], f2[1]];
  const answer = Calculator.fractionAdd(f1, f3);
  return answer;
};
Calculator.fractionMultiply = function (f1, f2, simplify = true) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  let answer = [f1[0] * f2[0], f1[1] * f2[1]];
  answer = Calculator.fractionSimplify(answer);
  return answer;
};
Calculator.fractionDivide = function (f1, f2, simplify = true) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  let answer = [f1[0] * f2[1], f1[1] * f2[0]];
  answer = Calculator.fractionSimplify(answer);
  return answer;
};
Calculator.fractionEquals = function (f1, f2) {
  return f1[0] * f2[1] == f1[1] * f2[0];
};
Calculator.fractionFuzzyEquals = function (f1, f2, precision) {
  if (precision == null) precision = 10;
  const v1 = f1[0] * f2[1];
  const v2 = f1[1] * f2[0];
  return Math.abs(v1 - v2) < Math.pow(0.1, precision);
};
Calculator.fractionSimplify = function (f) {
  const d = Math.abs(Calculator.gcd(f[0], f[1]));
  if (d == 0) return f;
  let answer = [f[0] / d, f[1] / d] as const;
  if (answer[1] < 0) answer = [answer[0] * -1, answer[1] * -1];
  return answer;
};
Calculator.fractionFuzzySimplify = function (f) {
  f = Calculator.asFraction(f);
  return Calculator.fractionSimplify([
    Calculator.round(f[0]),
    Calculator.round(f[1]),
  ]);
};
Calculator.asFraction = function (value) {
  if (Calculator.isFraction(value)) return value;
  else return [value, 1];
};
Calculator.asDecimal = function (value) {
  if (Calculator.isFraction(value)) return value[0] / value[1];
  else return value;
};
Calculator.setDenominator = function (f, denominator) {
  const f1 = Calculator.asFraction(f);
  const mod = denominator / f1[1];
  const f2 = [mod * f1[0], mod * f1[1]];
  if (Calculator.getPrecision(f2[0]) < 1) {
    return f2;
  } else {
    return f1;
  }
};
Calculator.toFractionIfRational = function (n, maxDenominator) {
  if (Calculator.equals(0, n)) {
    return 0;
  } else if (Calculator.isFraction(n)) {
    return Calculator.fractionFuzzySimplify(n);
  } else if (Calculator.isNumber(n)) {
    for (let d = 1; d < maxDenominator; d++) {
      if (Calculator.getPrecision(n * d) < 1) {
        return Calculator.fractionFuzzySimplify([n * d, d]);
      }
    }
    return n;
  } else {
    TestUtil.warn('Calculator::toFractionIfRational: invalid number ' + n);
  }
};

Calculator.fixNumber = function (n) {
  if (Calculator.VERBOSE) TestUtil.log('Calculator.fixNumber', n);
  if (Calculator.isDefined(n)) return Calculator.toFractionIfRational(n, 1000);
  else return 0;
};

/* Abstracted Operations */
Calculator.getOpString = function (op) {
  let opString = '';
  switch (op) {
    case Calculator.ADD:
      opString = '+';
      break;
    case Calculator.SUB:
      opString = '-';
      break;
    case Calculator.MUL:
      opString = '×';
      break;
    case Calculator.DIV:
      opString = '÷';
      break;
  }
  return opString;
};
Calculator.performOperation = function (
  val1: number | number[],
  op: number,
  val2: number | number[] | null = null,
  simplify = true,
) {
  if (simplify == null) simplify = true;
  let val: number | number[] | null = null;
  switch (op) {
    case Calculator.ADD:
      val = Calculator.add(val1, val2, simplify);
      break;
    case Calculator.SUB:
      val = Calculator.subtract(val1, val2, simplify);
      break;
    case Calculator.MUL:
      val = Calculator.multiply(val1, val2, simplify);
      break;
    case Calculator.DIV:
      val = Calculator.divide(val1, val2, simplify);
      break;
    case Calculator.SQUR:
      val = Calculator.square(val1, simplify);
      break;
    case Calculator.SQRT:
      val = Calculator.squareroot(val1, simplify);
      break;
    case Calculator.CUBE:
      val = Calculator.cube(val1, simplify);
      break;
    case Calculator.CBRT:
      val = Calculator.cuberoot(val1, simplify);
      break;
    default:
      TestUtil.warn("Invalid op code: '" + op + "'");
  }
  return val;
};
Calculator.equals = function (v1, v2) {
  return Calculator.fractionEquals(
    Calculator.asFraction(v1),
    Calculator.asFraction(v2),
  );
};
Calculator.fuzzyEquals = function (v1, v2, precision = 10) {
  if (precision == null) precision = 10;
  return Calculator.fractionFuzzyEquals(
    Calculator.asFraction(v1),
    Calculator.asFraction(v2),
    precision,
  );
};
Calculator.equalsIn = function (val, arr) {
  for (const i in arr) {
    if (Calculator.equals(val, arr[i])) return true;
  }
  return false;
};
Calculator.add = function (v1, v2, simplify = true) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  if (Calculator.isFraction(v1) || Calculator.isFraction(v2)) {
    return Calculator.fractionAdd(
      Calculator.asFraction(v1),
      Calculator.asFraction(v2),
    );
  } else {
    return v1 + v2;
  }
};
Calculator.subtract = function (v1, v2, simplify?) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  if (Calculator.isFraction(v1) || Calculator.isFraction(v2)) {
    return Calculator.fractionSubtract(
      Calculator.asFraction(v1),
      Calculator.asFraction(v2),
    );
  } else {
    return v1 - v2;
  }
};
Calculator.multiply = function (v1, v2, simplify = true) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  if (Calculator.isFraction(v1) || Calculator.isFraction(v2)) {
    return Calculator.fractionMultiply(
      Calculator.asFraction(v1),
      Calculator.asFraction(v2),
    );
  } else {
    return v1 * v2;
  }
};
Calculator.divide = function (v1, v2, simplify?) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  if (Calculator.isFraction(v1) || Calculator.isFraction(v2)) {
    return Calculator.fractionDivide(
      Calculator.asFraction(v1),
      Calculator.asFraction(v2),
    );
  } else {
    if (v2 == 0) {
      //Prevent Division By Zero.
      return null;
    } else {
      return v1 / v2;
    }
  }
};
Calculator.square = function (value, simplify?) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  if (Calculator.isFraction(value)) {
    return Calculator.multiply(value, value);
  } else {
    return value * value;
  }
};
Calculator.squareroot = function (value, simplify?) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  if (Calculator.isFraction(value)) {
    const sqrt = [Math.sqrt(value[0]), Math.sqrt(value[1])];
    return Calculator.fractionSimplify(sqrt);
  } else {
    return Math.sqrt(value);
  }
};
Calculator.cube = function (value, simplify?) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  if (Calculator.isFraction(value)) {
    return Calculator.multiply(value, Calculator.multiply(value, value));
  } else {
    return value * value * value;
  }
};
Calculator.cuberoot = function (value, simplify = true) {
  if (!Calculator.isDefined(simplify)) simplify = true;
  if (Calculator.isFraction(value)) {
    const sqrt = [Calculator.cuberoot(value[0]), Calculator.cuberoot(value[1])];
    return Calculator.fractionSimplify(sqrt);
  } else {
    return Math.pow(value, 1 / 3);
  }
};

/* Algebra */

Calculator.evaluate = function (expression, varValues, simplify = true) {
  if (Calculator.VERBOSE)
    TestUtil.log('Calculator.evaluate::', expression, varValues, simplify);
  if (!Calculator.isDefined(simplify)) simplify = true;
  if (Calculator.isFraction(expression)) {
    const f = [
      Calculator.evaluate(expression[0], varValues),
      Calculator.evaluate(expression[1], varValues),
    ];
    if (simplify) return Calculator.fractionSimplify(f);
    else return f;
  } else {
    return Calculator.evaluateString(expression, varValues);
  }
};
Calculator.evaluateString = function (expression, varValues) {
  if (Calculator.VERBOSE)
    TestUtil.log('Calculator.evaluateString::' + expression, varValues);
  let value;

  /*
	var evalStr = "";
	for(var v in varValues) {
		evalStr += v+"="+varValues[v]+";";
	}
	evalStr += "var value="+Calculator.fixExpressionString(expression)+";";
	eval(evalStr);
	value = eval("value");
	*/

  const paramValues = Object.assign({}, varValues);
  const parsed = parse(Calculator.fixExpressionString(expression));
  value = parsed.evaluate(paramValues);

  //TestUtil.logObject(paramValues);
  //TestUtil.logObject(parsed);

  if (Calculator.isNumber(value)) return value;
  else return 0;
};
Calculator.getExpressionVars = function (expression) {
  const vars = [] as string[];
  const symbols = ['x', 'y', 'z'];
  for (let i = 0; i < symbols.length; i++) {
    const letter = symbols[i]!;
    if (Calculator.isFraction(expression)) {
      if (
        expression[0].indexOf(letter) >= 0 ||
        expression[1].indexOf(letter) >= 0
      )
        vars.push(letter);
    } else {
      if (expression.indexOf(letter) >= 0) vars.push(letter);
    }
  }
  return vars;
};

Calculator.fixExpressionString = function (expression) {
  let pattern;

  // Use ** Instead of ^, although may have to visit Math.pow option if IE11 support is needed.
  expression = expression.replace(/\@/g, '^2');
  expression = expression.replace(/\#/g, '^3');
  expression = expression.replace(/\$/g, '^4');
  expression = expression.replace(/\%/g, '^5');

  // [...] to (...)
  expression = expression.replace(/\[/g, '(');
  expression = expression.replace(/\]/g, ')');

  // xx^2 to (x*x^2)
  expression = expression.replace(/(xx)\^([0-9])/g, 'x^1x^$2');

  // yy^2 to (y*y^2)
  expression = expression.replace(/(yy)\^([0-9])/g, 'y^1y^$2');

  // zz^2 to (z*z^2)
  expression = expression.replace(/(zz)\^([0-9])/g, 'z^1z^$2');

  // x^2 to (x^2)
  expression = expression.replace(/([xyz])\^([0-9])/g, '($1^$2)');

  // x^2 to (x^2)
  expression = expression.replace(
    /(\([xyz]\^[0-9]\))(\([xyz]\^[0-9]\))/g,
    '($1*$2)',
  );

  // x^2 to (x^2)
  expression = expression.replace(/([0-9])\(([xyz])\^([0-9])\)/g, '($1*$2^$3)');

  // 2x to 2*x, xx to x*x, etc.
  pattern = /([0-9xyz]+)([xyz])/g;
  let newExpression = expression;
  do {
    expression = newExpression;
    newExpression = expression.replace(pattern, '($1*$2)');
  } while (newExpression != expression);

  // x(...) to x*(...), 2(...) to 2*(...), etc.
  pattern = /([0-9xyz])([\(])/g;
  expression = expression.replace(pattern, '$1*(');

  // (...)x to (...)*x, (...)2 to (...)*2, etc.
  pattern = /([\)])([0-9xyz])/g;
  expression = expression.replace(pattern, ')*$2');

  // (...)(...) to (...)*(...)
  expression = expression.replace(/\)\(/g, ')*(');
  // remove spaces
  expression = expression.replace(/ /g, '');

  //This is not correct.  It does not capture (3x)^2 for example
  // (x^2) to Math.pow(x,2)
  //expression = expression.replace(/\(([0-9xyz])\^([0-9])\)/g, "Math.pow($1, $2)");

  if (Calculator.VERBOSE)
    TestUtil.log('Calculator.fixExpressionString::' + expression);

  return expression;
};
/*
Calculator.fixExpressionString = function(expression) {
	var pattern;
	var matches;
	pattern = /([0-9]|x|y|z)+\(?/g;
	matches = expression.match(pattern);
	for(match in matches) {
		expression = Calculator.fixTerm(match, expression);
	}
	pattern = /([x]+)\(/g;
	expression = expression.replace(pattern, "x*(");
	pattern = /([y]+)\(/g;
	expression = expression.replace(pattern, "y*(");
	pattern = /([z]+)\(/g;
	expression = expression.replace(pattern, "z*(");
	expression = expression.replace(")(", ")*(");
	return expression;
}
Calculator.fixTerm = function(term, expression) {
	var pattern = /([0-9]|x|y|z|\()/g;
	var matches = term.match(pattern);
	var newTerm = matches.join("*");
	expression = expression.replace(term, newTerm);
	return expression;
}
*/
